/**
 * entry.tsx
 * The bundle entry point. Mounts <OrbiDock /> ONCE into #orbi-dock-root
 * and publishes the imperative handle on window so the legacy index.html
 * code can prime customer context, change persona, and open/close the
 * dock without round-tripping through DOM events.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { OrbiDock, type OrbiDockHandle, type CustomerPriming, type SituationPriming } from './OrbiDock';
import dockCss from './OrbiDock.css?inline';
import { mountCards, observeCards, updateCard } from './src/cards/mount';

function injectStyles() {
  const STYLE_ID = 'orbi-dock-styles';
  if (document.getElementById(STYLE_ID)) return;
  const tag = document.createElement('style');
  tag.id = STYLE_ID;
  tag.textContent = dockCss;
  document.head.appendChild(tag);
}

declare global {
  interface Window {
    OrbiDock?: OrbiDockHandle & {
      _ready: Promise<OrbiDockHandle>;
    };
  }
}

const ROOT_ID = 'orbi-dock-root';

/**
 * PHASE 3 (decision C): route-independent mount, feature-flagged OFF by default.
 *
 * teglo-dashboard is a multi-page app today. Every navigation is a full document
 * load, so there is no client-side router to mount the orb above, and a LIVE Vapi
 * voice call cannot survive a route change (the JS context is destroyed with the
 * page). What DOES survive is the ask chat and the folded voice transcript, which
 * OrbiDock mirrors to sessionStorage and restores on the next mount, so the panel
 * re-opens where the operator left it. That persistence already works and is
 * unchanged here.
 *
 * The mount-once guard below (container.dataset.orbiMounted) is the single-Vapi
 * protection: it prevents a second React root, and therefore a second Vapi/Krisp
 * init, if mount() ever runs twice against a container that is already live. A
 * future SPA shell that keeps #orbi-dock-root in the DOM across route changes gets
 * route-independent persistence, including the ONE live call, for free through
 * that same guard, with no re-init.
 *
 * This flag is the switch that future shell will read to opt into the persistent
 * mount. It is OFF by default and a deliberate no-op in the current MPA: it never
 * changes today's behaviour and never re-inits Vapi. Full cross-page live-voice
 * persistence is deferred to that shell (dispatch Phase 3 decision C).
 */
const PERSISTENT_MOUNT: boolean =
  (typeof window !== 'undefined') &&
  (((window as any).__ORBI_ENV__?.ORBI_PERSISTENT_MOUNT === true) ||
   (() => { try { return localStorage.getItem('orbi_persistent_mount') === '1'; } catch { return false; } })());

/**
 * PHASE 3: with ORBI_PERSISTENT_MOUNT on, the mount is ROUTE-INDEPENDENT - a
 * page that never declared #orbi-dock-root still gets the dock, because the
 * container is created on demand. That makes the dock's presence a property of
 * the app, not of each page template, which is exactly the contract a future
 * SPA shell needs: the shell keeps this one node (and its single Vapi engine)
 * alive across routes, and the mount-once guard below ensures nothing ever
 * re-inits. Flag off = today's behaviour, byte for byte.
 */
function ensureContainer(): HTMLElement | null {
  let container = document.getElementById(ROOT_ID);
  if (!container && PERSISTENT_MOUNT && document.body) {
    container = document.createElement('div');
    container.id = ROOT_ID;
    document.body.appendChild(container);
  }
  return container;
}

/**
 * JARVIS NAV: inside the in-place workspace frame there must be ONE dock and
 * ONE Vapi engine - the parent's. The workspace stamps its iframe with
 * data-jarvis-workspace="1" (same origin, so frameElement is readable), and
 * the parent injects its own window.OrbiDock into the frame, so the framed
 * page's subject priming still lands on the live dock. Skipping the mount
 * here is what makes that safe.
 */
function insideWorkspaceFrame(): boolean {
  try {
    const fe = window.frameElement as HTMLElement | null;
    return !!fe && fe.dataset?.jarvisWorkspace === '1';
  } catch {
    return false;
  }
}

function mount() {
  if (insideWorkspaceFrame()) {
    console.log('[OrbiDock] workspace frame: dock mount skipped (parent dock owns this session)');
    return;
  }
  const container = ensureContainer();
  if (!container) {
    console.warn(`[OrbiDock] mount point #${ROOT_ID} not found`);
    return;
  }
  if (container.dataset.orbiMounted === '1') return;
  container.dataset.orbiMounted = '1';
  // Expose the persistent-mount intent on the container so a future SPA shell can
  // detect whether to keep this node (and its single Vapi instance) alive across
  // routes. Off by default; a no-op marker in today's MPA.
  container.dataset.orbiPersistentMount = PERSISTENT_MOUNT ? '1' : '0';
  /* JARVIS DARK-LOCK: the dock's own chrome (OrbiDock.css) always renders
     dark, independent of the dashboard's own <html data-theme> — ChromeOrb's
     canvas is itself theme-blind and only reads right against a dark panel.
     Stamping data-theme="dark" here (never on <html>, so nothing else on the
     page is touched) makes every [data-theme="dark"] token rule resolve
     locally on this element and cascade to the button and side panel
     rendered inside it. Mirroring data-vertical-slug from <html> (set once,
     early, by ora-config.js) lets a per-vertical dark palette — e.g.
     tokens/legal.dark.css's [data-theme="dark"][data-vertical-slug="legal"]
     — apply here too, instead of always falling back to Teglo's default
     mint dark theme. Brand accent is unaffected: OrbiDock.css explicitly
     inherits --color-accent/-bright/-soft back from <html> past this lock. */
  container.setAttribute('data-theme', 'dark');
  /* GENUI-1 (2026-10-08): the sharpened card and action chrome in OrbiDock.css is scoped to
     [data-genui-v2="1"] and stamped here only when the bundle was built with VITE_GENUI_V2=1,
     so a default build renders the dock byte for byte as before. No behaviour change either way. */
  if (String(import.meta.env.VITE_GENUI_V2 ?? '') === '1') container.setAttribute('data-genui-v2', '1');
  const verticalSlug = document.documentElement.getAttribute('data-vertical-slug');
  if (verticalSlug) container.setAttribute('data-vertical-slug', verticalSlug);
  injectStyles();

  let resolveReady: (h: OrbiDockHandle) => void = () => {};
  const ready = new Promise<OrbiDockHandle>((res) => {
    resolveReady = res;
  });

  const queued: Array<(h: OrbiDockHandle) => void> = [];

  /* A3 SECONDARY FIX: this used to be Object.assign(queueingMethods, noopHandle,
     { _ready }) — sources apply left to right, so the no-op handle OVERWROTE
     every queueing method and `queued` could never receive a call. Any host-page
     bridge call landing before React mounted (the customers page's ?account=
     deep-link auto-open — exactly what a Jarvis navigation produces) was
     silently dropped. The queueing methods ARE the pre-mount handle; there is
     nothing for a no-op fallback to do that queueing doesn't do better. */
  window.OrbiDock = Object.assign(
    {
      setCustomer: (c: CustomerPriming | null) => { queued.push((h) => h.setCustomer(c)); },
      setPersona:  (k: string) => { queued.push((h) => h.setPersona(k)); },
      open:        () => { queued.push((h) => h.open()); },
      close:       () => { queued.push((h) => h.close()); },
      callCustomer:(c: CustomerPriming | null) => { queued.push((h) => h.callCustomer(c)); },
      showCard:    (t: string, p?: any) => { queued.push((h) => h.showCard(t, p)); },
      openSituation:(s: SituationPriming) => { queued.push((h) => h.openSituation(s)); },
    },
    { _ready: ready }
  ) as Window['OrbiDock'];

  const onHandle = (h: OrbiDockHandle) => {
    window.OrbiDock = Object.assign(h, { _ready: ready });
    while (queued.length) {
      const fn = queued.shift();
      if (fn) {
        try { fn(h); } catch (err) { console.warn('[OrbiDock] queued call threw', err); }
      }
    }
    resolveReady(h);
  };

  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <OrbiDock registerHandle={onHandle} />
    </React.StrictMode>
  );
}

/**
 * Card islands (2026-08-29). The same bundle, and therefore the same React
 * runtime, now also hydrates any [data-ora-card] placeholder on the page.
 *
 * WHY HERE AND NOT IN A BUNDLE OF ITS OWN. A second bundle would inline its
 * own React, so a page carrying the dock and one card would run two React
 * runtimes against one document. One bundle costs bytes; two runtimes cost
 * correctness. The measured size delta is recorded in the dispatch.
 *
 * The dock's own mount is untouched: cards mount independently, and a page
 * with no #orbi-dock-root still gets its cards, exactly as a page with no
 * cards still gets its dock.
 */
function boot() {
  mount();
  /* The vanilla renderer calls window.OraCards.update(el) the moment it
     writes a mount point, so a card painted after this bundle loaded is
     hydrated synchronously instead of waiting for the observer's microtask.
     The observer still exists for the reverse order (card written first). */
  (window as any).OraCards = { mount: mountCards, update: updateCard };
  try {
    mountCards();
    /* The vanilla pages repaint card bodies on lens change, time-range
       change and poll, so a one-shot scan would hydrate the first render
       and miss every one after it. */
    observeCards();
  } catch (err) {
    console.warn('[ora-card] mount failed', err);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
